<!-- Orbi PatientResults card (PCSA-13224). Behavior + data mirror mobile
     (orbi_results_card.dart / NewResultCardContent); the layout follows the web
     Figma ("AI Agent Canvas"), whose two frames are structurally different cards:

       Single Accession — one card: vial glyph + "Results from MM/DD/YYYY",
       provider line, bulleted tests, then View Details / View All Results.

       Multi Accession — one card: vial glyph + "Results", owning a stack of
       BORDERED inner cards ("Ordered Tests:" / "Order From:" / "Purchase Date:"
       + status pill + "View >"), with a shared Show N More / View All Results.

     Each frame's wide and narrow variants are breakpoints of the same markup.
     What differs between them, measured off the frames:
       - the footer runs INLINE when wide and STACKS when narrow;
       - narrow puts "Show N More" ABOVE the buttons, centered, where wide trails
         it at the end of the button row;
       - the single frame's test list is two columns wide, one narrow;
       - a multi inner card's several tests are an inline bullet-separated RUN
         when wide and a real bulleted LIST when narrow. -->

<!-- Single accession. -->
<section *ngIf="isSingle" class="orbi-results-card" aria-label="Lab results">
  <div class="orbi-results-card__card">
    <div class="orbi-results-card__head">
      <!-- The Figma's own vial glyph, exported from the frame (design review
           08/11). NOT a Material ligature: the frame draws a CAPPED VIAL, and the
           bundled material-icons font has no `labs` glyph — only `science`, a
           conical flask, which is what this card used to render. -->
      <img class="orbi-results-card__icon" src="assets/images/orbi-results-vial-icon.svg" alt="" aria-hidden="true" />
      <h3 class="orbi-results-card__title">{{ cardTitle(single) }}</h3>
      <span
        *ngIf="badgeLabel(single) as badge"
        class="orbi-results-card__badge"
        [class.orbi-results-card__badge--updated]="badge === 'Updated'"
        [class.orbi-results-card__badge--unread]="badge === 'Unread'"
        >{{ badge }}</span
      >
    </div>

    <!-- Provider line. Figma draws this at the card's HEADING weight (mobile
         passes `LabcorpUITypography.h6`), not as muted secondary copy. -->
    <p *ngIf="providerLine(single)" class="orbi-results-card__provider">{{ providerLine(single) }}</p>

    <ul *ngIf="visibleTests(single).length" class="orbi-results-card__tests">
      <li *ngFor="let test of visibleTests(single)" class="orbi-results-card__test">{{ test }}</li>
    </ul>

    <!-- Footer CTAs at `size="small"`, on the SAME 32px scale as the appointment
         cards (design review 08/11 — the card was drawn at the 48px default and
         read too heavy beside its own body copy). The Figma node states
         `height:32px; min-width:128px; radius:16px; padding:7px 13px`, and
         `btn-sm` resolves to exactly that: $btn-padding-y-sm .375rem (6px) + the
         1px border = 7px, $btn-padding-x-sm .75rem (12px) + 1px = 13px, 6+6+18+2
         = 32px tall, and $btn-border-radius-sm 1rem = 16px. `minWidth` is left at
         ui-button's own default of 128 — the Figma `min-width` — instead of the
         0 it used to be forced to. Geometry stays derived from the design system
         rather than pinned to literals. -->
    <div class="orbi-results-card__actions">
      <!-- Show N More. A bare text+chevron control rather than a <ui-button>: the
           shared button renders its icon BEFORE the label and Figma trails the
           chevron here (it points at the disclosure below, so the side carries
           meaning), and `btn-link` would still not place the glyph after it.

           FIRST in the DOM because that is the narrow frame's visual order — the
           toggle sits centered ABOVE the stacked buttons — and because it belongs
           beside the list it discloses when read in sequence. The wide frame
           trails it at the end of the button row, which the footer does with
           `order` rather than by repeating this markup. -->
      <button
        *ngIf="hasMore(single)"
        type="button"
        class="orbi-results-card__more"
        [attr.aria-expanded]="isExpanded(single)"
        (click)="toggleExpanded(single)"
      >
        {{ testsToggleLabel(single) }}
        <span class="material-icons-round" aria-hidden="true">{{ isExpanded(single) ? 'expand_less' : 'expand_more' }}</span>
      </button>
      <ui-button
        *ngIf="single.isDetailAvailable !== false"
        class="orbi-results-card__btn"
        buttonText="View Details"
        rank="primary"
        size="small"
        (handleClick)="onViewDetails(single)"
      ></ui-button>
      <!-- `secondary` is the white/blue-outlined rank, NOT the cyan its base
           `.btn-secondary` implies: labcorp-bootstrap re-declares `.btn.btn-secondary`
           later in the cascade as `#FFFFFF` on a `#3A5CE9` border in `#3A5CE9` text,
           which is this button's Figma spec exactly. -->
      <ui-button
        class="orbi-results-card__btn"
        buttonText="View All Results"
        rank="secondary"
        size="small"
        (handleClick)="onViewAll()"
      ></ui-button>
    </div>
  </div>
</section>

<!-- Multiple accessions: ONE card owning a stack of bordered inner cards. -->
<section *ngIf="!isSingle && accessions.length" class="orbi-results-card orbi-results-card--multi" aria-label="Lab results">
  <div class="orbi-results-card__card">
    <div class="orbi-results-card__head">
      <img class="orbi-results-card__icon" src="assets/images/orbi-results-vial-icon.svg" alt="" aria-hidden="true" />
      <h3 class="orbi-results-card__title">{{ groupTitle }}</h3>
    </div>

    <div
      *ngFor="let accession of visibleAccessions; trackBy: trackById"
      #accessionCard
      class="orbi-results-card__accession"
      role="group"
      [attr.aria-label]="cardTitle(accession)"
    >
      <!-- "Ordered Tests:" — one test always sits inline after the label. SEVERAL
           are drawn two ways by the Figma, and both are the same data:

             wide   — an inline run separated by bullets ("Iron • Ferritin • …"),
                      starting on the label's own line and wrapping to the card's
                      full width;
             narrow — a real bulleted list indented beneath the label.

           Both are emitted and the breakpoint reveals one, rather than picking in
           TypeScript: which one applies is a function of the rendered width, which
           the component cannot see, and a resize has to switch between them
           without re-running any logic. `aria-hidden` on the run keeps the hidden
           copy out of the accessibility tree, so a screen reader hears the list
           once at either width.

           The status pill shares this first row, at its trailing edge. -->
      <div class="orbi-results-card__row orbi-results-card__row--head">
        <!-- The pill comes FIRST in the source because it floats: a float only
             displaces the text that FOLLOWS it, and the frame's first line stops
             short to clear this pill while the later lines run full width. -->
        <span class="orbi-results-card__status" [class.orbi-results-card__status--updated]="accessionStatus(accession) === 'Updated'">{{
          accessionStatus(accession)
        }}</span>
        <ng-container *ngIf="orderedTests(accession).length">
          <span class="orbi-results-card__label">Ordered Tests:</span>
          <ng-container *ngIf="hasTestList(accession); else singleTest">
            <span class="orbi-results-card__test-run" aria-hidden="true">{{ testRun(accession) }}</span>
            <ul class="orbi-results-card__row-tests">
              <li *ngFor="let test of orderedTests(accession)" class="orbi-results-card__test">{{ test }}</li>
            </ul>
          </ng-container>
          <ng-template #singleTest>
            <span class="orbi-results-card__value">{{ orderedTests(accession)[0] }}</span>
          </ng-template>
        </ng-container>
      </div>

      <div *ngIf="orderFrom(accession)" class="orbi-results-card__row">
        <span class="orbi-results-card__label">Order From:</span>
        <span class="orbi-results-card__value">{{ orderFrom(accession) }}</span>
      </div>

      <!-- Purchase Date shares its row with the "View >" link, which floats to the
           trailing edge (source-first, like the pill above). Wide runs label +
           value + link on one baseline; narrow drops the VALUE onto its own line
           and the link keeps that value's baseline — the label goes block-level
           below XL, see `&__row--footer &__label` in the stylesheet. -->
      <div class="orbi-results-card__row orbi-results-card__row--footer">
        <ng-container *ngIf="purchaseDate(accession)">
          <span class="orbi-results-card__label">Purchase Date:</span>
          <span class="orbi-results-card__value">{{ purchaseDate(accession) }}</span>
        </ng-container>
        <!-- LAST in source, unlike the pill above: a right float is placed on the
             line box where it is encountered, so following the value puts it on
             the VALUE's line — which is what both frames draw (narrow keeps
             "mm/dd/yyyy" and "View ›" on one baseline under the label). Leading
             the row instead would strand it up on the label's line.

             A <ui-button rank="link"> (PR 3170 review), which is what the Figma
             node calls this control. The trailing chevron and the per-accession
             accessible name come from the shared component's own `iconRight` /
             `ariaLabel` inputs — the glyph points at the detail view, so its side
             carries meaning, and "View" alone is not distinguishing when a stack
             renders one of these per accession. The stylesheet then strips
             `btn-link`'s padding and min-width so the control still floats as
             text on the value's baseline. -->
        <ui-button
          *ngIf="accession.isDetailAvailable !== false"
          class="orbi-results-card__view"
          buttonText="View"
          rank="link"
          size="small"
          iconRight="arrow_forward_ios"
          [minWidth]="0"
          [ariaLabel]="'View ' + cardTitle(accession)"
          (handleClick)="onViewDetails(accession)"
        ></ui-button>
      </div>
    </div>

    <!-- Shared footer: Show N More over the accession STACK (the single frame's
         toggle counts tests instead), then one View All Results for the set. Same
         DOM order and same `order` reordering as the single frame — narrow centers
         the toggle above the CTA, wide runs them as one row with the CTA leading
         and the toggle at the trailing edge. -->
    <div class="orbi-results-card__actions orbi-results-card__actions--multi">
      <button
        *ngIf="hasMoreAccessions"
        type="button"
        class="orbi-results-card__more"
        [attr.aria-expanded]="isAccessionsExpanded"
        (click)="toggleAccessionsExpanded()"
      >
        {{ accessionsToggleLabel }}
        <span class="material-icons-round" aria-hidden="true">{{ isAccessionsExpanded ? 'expand_less' : 'expand_more' }}</span>
      </button>
      <ui-button
        class="orbi-results-card__btn orbi-results-card__view-all"
        buttonText="View All Results"
        rank="primary"
        size="small"
        (handleClick)="onViewAll()"
      ></ui-button>
    </div>
  </div>
</section>
